import { useEffect, useState } from "react";

type Whoami = {
  ip: string;
  family: string;
  isp: string | null;
  asn: number | null;
  country: string | null;
  region: string | null;
  city: string | null;
  postalCode: string | null;
  timezone: string | null;
  latitude: string | null;
  longitude: string | null;
  colo: string | null;
  httpProtocol: string | null;
  tlsVersion: string | null;
  userAgent: string | null;
};

export function WhatIsMyIp() {
  const [data, setData] = useState<Whoami | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch("/api/whoami")
      .then((r) => r.json())
      .then((d) => setData(d as Whoami))
      .catch(() => setError("Couldn't read your connection details."));
  }, []);

  const copy = () => {
    if (!data?.ip) return;
    navigator.clipboard?.writeText(data.ip).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  const place = data ? [data.city, data.region, data.country].filter(Boolean).join(", ") : "";

  return (
    <main className="console">
      <div className="input-row">
        <span className="input-row-label">MY IP</span>
        <span className="gen-intro">
          Your public IP address as the internet sees it, with the network it belongs to.
        </span>
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <div className="body-grid body-grid-single">
        <section className="table-panel">
          <div className="myip-hero">
            <span className="myip-label">Your public IP address</span>
            <div className="myip-value">
              <code>{data ? data.ip || "unknown" : "…"}</code>
              {data?.ip ? (
                <button type="button" className="gen-copy" onClick={copy}>
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              ) : null}
            </div>
            {data ? <span className="myip-family">{data.family}</span> : null}
          </div>

          {data ? (
            <>
              <table className="health-table">
                <tbody>
                  <Row label="ISP / network" value={data.isp} />
                  <Row label="ASN" value={data.asn ? `AS${data.asn}` : null} link={data.asn ? `/whois-lookup?query=AS${data.asn}` : undefined} />
                  <Row label="Approximate location" value={place || null} />
                  <Row label="Postal code" value={data.postalCode} />
                  <Row label="Timezone" value={data.timezone} />
                  <Row label="Coordinates" value={data.latitude && data.longitude ? `${data.latitude}, ${data.longitude}` : null} />
                  <Row label="Cloudflare edge" value={data.colo ? `${data.colo} (nearest data centre)` : null} />
                  <Row label="Connection" value={[data.httpProtocol, data.tlsVersion].filter(Boolean).join(" · ") || null} />
                </tbody>
              </table>

              <p className="myip-note">
                <strong>About that location.</strong> It's derived from who the address is registered
                to, not from your device — no GPS, no permission prompt. It's usually accurate to the
                city and often wrong about the neighbourhood. Behind a VPN or proxy you're seeing the
                exit node, not your own connection.
              </p>

              <p className="whois-cross">
                Look up who owns this address:{" "}
                <a href={`/whois-lookup?query=${encodeURIComponent(data.ip)}`}>IP WHOIS</a>
                {data.asn ? <> · <a href={`/whois-lookup?query=AS${data.asn}`}>AS{data.asn}</a></> : null} · or{" "}
                <a href="/subnet-calculator">work out its subnet</a>.
              </p>
            </>
          ) : !error ? (
            <div className="empty-state"><p>Reading your connection…</p></div>
          ) : null}
        </section>
      </div>
    </main>
  );
}

function Row({ label, value, link }: { label: string; value: string | null; link?: string }) {
  return (
    <tr className="health-row">
      <td className="ht-type">{label}</td>
      <td className="ht-records">
        {value ? (link ? <a href={link}><code>{value}</code></a> : <code>{value}</code>) : <span className="ht-empty">not reported</span>}
      </td>
    </tr>
  );
}
